import React, { Suspense } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import 'react-data-grid/lib/styles.css'
import styles from './WorkpapersPreviewer.sass'
import Button from 'components/Button'
import WorkpapersPreviewerViewModel, {
  DocumentBlob,
} from './WorkpapersPreviewerViewModel'
import Spinner from 'components/Spinner'
import { match } from 'utils/pattern-match-util'

const SpreadSheetPreviewer = React.lazy(
  () =>
    import(
      /* webpackChunkName: 'spreadsheet-previewer' */ './SpreadSheetPreviewer'
    )
)

interface Props {
  vm: WorkpapersPreviewerViewModel
}

export default observer(function WorkpapersPreviewer({ vm }: Props) {
  if (vm.downloadBlob.pending) {
    return (
      <div className={cx(styles.contentPreview)}>
        <Spinner thickness={4} size={80} />
      </div>
    )
  }

  return match(vm.previewKind, {
    PDF: () => vm.blob && <PDFPreviewer blob={vm.blob} />,
    Image: () => vm.blob && <ImagePreviewer blob={vm.blob} />,
    SpreadSheet: () =>
      vm.blob && vm.blob.size < 10_000_000 /* 10MB */ ? (
        <Suspense fallback={<></>}>
          <SpreadSheetPreviewer blob={vm.blob} />
        </Suspense>
      ) : (
        <PreviewUnavailable onDownloadClick={vm.download}>
          The file is too big to preview.
        </PreviewUnavailable>
      ),
    Unavailable: () => (
      <PreviewUnavailable onDownloadClick={vm.download}>
        This file format is currently not supported.
      </PreviewUnavailable>
    ),
  })
})

interface PreviewerProps {
  blob: DocumentBlob
}

const ImagePreviewer = observer(function PDFPreviewer({
  blob,
}: PreviewerProps) {
  return (
    <div className={cx(styles.contentPreview)}>
      <img className={styles.contentPreviewImg} src={blob.url} />
    </div>
  )
})

const PDFPreviewer = observer(function PDFPreviewer({ blob }: PreviewerProps) {
  return (
    <object
      className={styles.contentPreviewPDF}
      data={blob.url}
      type="application/pdf"
    />
  )
})

interface PreviewUnavailableProps {
  children: React.ReactNode
  onDownloadClick: () => void
}

const PreviewUnavailable = observer(function PreviewUnavailable({
  children,
  onDownloadClick,
}: PreviewUnavailableProps) {
  return (
    <div
      className={cx(styles.contentPreview)}
      data-test="workpaper-preview-unavailable"
    >
      <div className={cx(styles.contentPreviewUnavailableTitle)}>
        Preview unavailable
        <span className={cx(styles.contentPreviewUnavailableHelp)}>
          {children} Download the file to complete the review.
        </span>
      </div>

      <Button type="text" onClick={onDownloadClick}>
        Download
      </Button>
    </div>
  )
})
